import {
  ListHookEditable,
  ListHookWithFilters,
  ListHookWithSelectables,
  Params
} from "../models/useSettingsList/types";
import { Role } from "@closerplatform/spinner-openapi";
import { flow } from "lodash";
import {
  useSettingsListEditable,
  withFilters,
  withSelectables
} from "../models/useSettingsList/useSettingsList";
import {
  copyExistingForm,
  createNewRoleForm,
  mapFormToCreateForm,
  mapFormToUpdateForm,
  mapRoleToForm
} from "./utils";
import { usePreloader } from "../../HOC/withPreloading/withPreloading";
import { RoleForm } from "./types";
import React, { createContext, useContext, useEffect } from "react";
import { useAgentServices } from "../../../models/agentServices";

type HookParams = Params<RoleForm, RoleForm, Role>;
type HookEditable = ListHookEditable<RoleForm, RoleForm>;
type HookSelectable = ListHookWithSelectables<RoleForm, RoleForm, HookEditable>;
type HookFilterable = ListHookWithFilters<RoleForm, RoleForm, HookSelectable>;

const useListHook = flow<[HookParams], HookEditable, HookSelectable, HookFilterable>(
  useSettingsListEditable,
  withSelectables,
  withFilters
);

const useRolesSettingsState = () => {
  const { setLoadingState } = usePreloader();
  const { spinnerService } = useAgentServices();

  const resetSelected = () => {
    listHook.deselectAll();
  };

  const listHook = useListHook({
    apiGetItems: () => spinnerService.getOrgRoles(),
    apiCreateItem: (form) => spinnerService.createRole(mapFormToCreateForm(form)),
    apiUpdateItem: (form) => spinnerService.updateRole(form.$.id.$, mapFormToUpdateForm(form)),
    apiDeleteItem: (id) => spinnerService.deleteRole(id),
    mapItemToForm: mapRoleToForm,
    createNewForm: createNewRoleForm,
    copyExistingForm,
    saveErrorId: "settings.error.cantSave",
    onSubmitCallback: resetSelected,
    onDiscardCallback: resetSelected,
    onInitializeResult: setLoadingState
  });

  useEffect(() => {
    listHook.items.forEach((item) => {
      const setEdited = () => listHook.setItemEdited(item);
      item.$.name.onUpdate(setEdited);
      item.$.permissions.onUpdate(setEdited);
    });
  }, [listHook.items]);

  return listHook;
};

const RolesSettingsContext = createContext<HookEditable | undefined>(undefined);

export const RolesSettingsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const values = useRolesSettingsState();

  return <RolesSettingsContext.Provider value={values}>{children}</RolesSettingsContext.Provider>;
};

export const useRolesSettings = () => {
  const context = useContext(RolesSettingsContext);

  if (context === undefined) {
    throw new Error("useAgentGroupsSettings must be used within a RolesSettingsProvider");
  }

  return context;
};
